import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { useDebounceValue } from 'usehooks-ts';
import { useFormik, FormikValues } from 'formik';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import {
  Utils,
  Messages,
  useAlert,
  useNavigate,
  ActionQueueType,
} from '@gv/triage-components';

import { api } from 'api';
import { cvtRoles } from 'config';
import { setLoading } from 'store/slices/loader';
import { updateForm } from 'store/api/action-queue';
import { getTokens, saveCaseForm } from 'utils/storage';
import { useAblyChannel, useDynamicFormProps } from 'hooks';
import { getDynamicFormInitialValues } from 'utils/initials';
import { useAblyChatChannel } from 'hooks/useAblyChatChannel';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { setIsShowFeedback } from 'store/slices/analytics-export';
import { selectAuthUser, selectHasRoles } from 'store/slices/auth';
import { setOnVideoCall, setVideoCallEnded } from 'store/slices/call';
import { updateCase, createUpdateSubmit } from 'store/api/cases/thunks';
import { changeStep, selectCaseFormStep } from 'store/slices/case-form-step';
import {
  pickUpStreamRoom,
  validateFormCallOutcome,
  getCaseFormValidationSchema,
} from 'utils/config';
import {
  getQueueItemId,
  getDynamicCaseData,
  getActionCenterCaseForm,
  getCaseFormStepWithError,
} from 'utils/helpers';
import {
  selectCurrentTask,
  selectCurrentTaskId,
  selectCurrentTaskForm,
  selectIsTaskSwitching,
} from 'store/api/action-queue/selectors';

import { useCommEntities } from '../hooks';

import { UseFormProps, UseFormReturnProps } from './types';

export const useForm = ({
  endTask,
  isCompletely,
  isCallTransferred,
  setCallTransferred,
}: UseFormProps): UseFormReturnProps => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(selectAuthUser);
  const task = useAppSelector(selectCurrentTask);
  const caseForm = useAppSelector(selectCurrentTaskForm);
  const currentTaskId = useAppSelector(selectCurrentTaskId);
  const isTaskSwitching = useAppSelector(selectIsTaskSwitching);
  const isCVT = useAppSelector(selectHasRoles(cvtRoles));
  const { client, hospital, hospitalId } = useCommEntities();
  const { activeVideoCall, isVideoCallEnded } = useSelector(
    (state: RootState) => state.call
  );

  const channel = useAblyChannel(
    process.env.REACT_APP_ABLY_CHANNEL ?? 'GuardianVets_Dev_Server_delta'
  );

  const ablyChatChannel = useAblyChatChannel(task?.channelName);

  console.log('ably channel updated', channel);

  const taskId = task ? getQueueItemId(task) : undefined;
  const isDynamicFlow = !!task?.h_is_dynamic_flow_enabled;

  const [formTaskId, setFormTaskId] = useState(taskId);
  const { token } = getTokens();

  const [isShowValidationEmail, setIsShowValidationEmail] = useState(false);

  const taskRef = useRef(task);
  const navigate = useNavigate();

  useEffect(() => {
    taskRef.current = task;
  }, [task]);

  const { handleError, showErrorAlert, showSuccessAlert } = useAlert();

  const { validationSchema, ...dynamicFormProps } = useDynamicFormProps({
    hospitalId,
  });
  const { resetSteps, passedSteps, passedStepsFieldValues } = dynamicFormProps;

  const showFeedback = async () => {
    const response: any =
      await api.customerFeedback.checkTypeFormHospital(hospitalId);
    dispatch(setIsShowFeedback(response.data?.data?.status));
    const isEditFeedbackEmail = location.pathname.includes('consultation/edit');
    setIsShowValidationEmail(
      response.data?.data?.status && !isEditFeedbackEmail
    );
  };

  useEffect(() => {
    showFeedback();
  }, []);

  const formValidationSchema = useMemo(
    () =>
      isDynamicFlow
        ? validationSchema
        : getCaseFormValidationSchema({
            isCVT,
            hospital,
            isShowValidationEmail,
          }),
    [isCVT, hospital, isDynamicFlow, validationSchema, isShowValidationEmail]
  );

  const initialFormikValues = useMemo(() => {
    const baseValues = isDynamicFlow
      ? getDynamicFormInitialValues(caseForm, client)
      : caseForm;
    return {
      ...baseValues,
      noneFieldName: false, // Initialize as false
    };
  }, [isDynamicFlow, client, caseForm]);

  const onSubmit = async (values: FormikValues): Promise<void> => {
    const startTime = task && task?.start_time;
    const endTime = task && task?.disconnected_at;
    const currentTime = Date.now();
    // Calculate the difference in seconds
    const timeDifferenceInSeconds =
      startTime && (currentTime - startTime) / 1000;
    const timeDifferenceInSecondsWithEnd =
      startTime && endTime && (endTime - startTime) / 1000;

    const NonDynamicValues = { ...values, client };

    const payload = isDynamicFlow
      ? getDynamicCaseData({
          caseForm,
          passedSteps,
          passedStepsFieldValues,
          values: { ...values, client },
        })
      : NonDynamicValues;
    const sleep = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    try {
      if (task?.type === ActionQueueType.Video) {
        pickUpStreamRoom({ startVideo: false, userId: task.u_id });
        await dispatch(
          createUpdateSubmit({
            task: task!,
            silent: true,
            isDynamicFlow,
            values: payload,
            isSubmitted: true,
          })
        ).unwrap();
        dispatch(setLoading(true));
        dispatch(setVideoCallEnded(false));
        if (!isVideoCallEnded) {
          activeVideoCall?.endCall();
          dispatch(setVideoCallEnded(true));
        }
        dispatch(setLoading(false));
        await endTask({ end: true, completely: true });
        await api.comm.userCallStatusUpdate({
          userId: authUser?.id,
          callSid: task.channelName,
          ParticipantLabel: 'Agent',
          callStatus: 'participant-leave',
        });
        await sleep(30000);
        const getCall = await activeVideoCall?.get();
        const recordingData = await activeVideoCall?.queryRecordings(
          getCall?.call?.session?.id
        );
        await api.videoCall.endVideoCall(
          authUser?.hospital_id,
          {
            status: 3,
            case_id: values.id,
            end_by: authUser?.id,
            room_id: task.channelName,
            file: recordingData?.recordings[0]?.url,
          },
          token,
          dispatch
        );
      } else {
        await dispatch(
          createUpdateSubmit({
            task: task!,
            isDynamicFlow,
            values: payload,
            isSubmitted: true,
          })
        ).unwrap();
      }
      if (isCompletely?.current) {
        if (task?.type === ActionQueueType.Chat) {
          await endTask({ end: true, completely: true, caseId: values.id });
          ablyChatChannel?.publish('end-chat', {
            status: '3',
            type: 'end-chat',
            case_id: values.id,
            end_by: authUser?.id,
            name: authUser?.name,
            hospital_id: hospitalId,
            room_id: task?.channelName,
          });
          await api.comm.userChatStatusUpdate({
            userId: authUser?.id,
            callSid: task.channelName,
            ParticipantLabel: 'Agent',
            callStatus: 'participant-leave',
          });
          dispatch(setOnVideoCall(true));
        } else {
          await endTask({ end: true, completely: true });
        }
      } else if (isCallTransferred) {
        await endTask({ completely: true });
        setCallTransferred(false);
      }
      if (isShowValidationEmail) {
        if (
          timeDifferenceInSecondsWithEnd &&
          timeDifferenceInSecondsWithEnd >= 30
        ) {
          await api.customerFeedback.sendFeedbackForm(
            values.id,
            values?.survey?.feedback_email,
            values?.callOutcome?.type
          );
        } else if (timeDifferenceInSeconds && timeDifferenceInSeconds >= 30) {
          await api.customerFeedback.sendFeedbackForm(
            values.id,
            values?.survey?.feedback_email,
            values?.callOutcome?.type
          );
        }
      }
      navigate('/action-center/queue');

      dispatch(setOnVideoCall(true));
      showSuccessAlert(Messages.CASE_SUBMITTED);
      dispatch(setVideoCallEnded(false));
      if (
        hospital?.type === 2 &&
        (task?.type === ActionQueueType.Video ||
          task?.type === ActionQueueType.Voice)
      ) {
        try {
          await api.subcription.sendFeedbackEmailVoice(hospitalId, values.id);
        } catch (err) {
          console.log('Error in sending feedback email', err);
        }
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const formShouldReset = formTaskId !== taskId;

  const form = useFormik({
    onSubmit,
    validateOnMount: true,
    enableReinitialize: formShouldReset,
    validationSchema: formValidationSchema,
    initialValues: initialFormikValues ?? {},
    validate: (values) =>
      validateFormCallOutcome(values, hospital?.timezone?.time_zone_code),
    onReset: () => {
      if (formShouldReset) {
        console.log(`update form task from ${formTaskId} to ${taskId}`);
        setFormTaskId(taskId);
        resetSteps();
      } else {
        console.log(`form reset w/o formShouldReset ${formTaskId} | ${taskId}`);
      }
    },
  });

  useEffect(() => {
    if (formShouldReset) {
      return;
    }

    console.log(
      'reset form like enableReinitialize with changing values only',
      caseForm ?? {}
    );

    form.resetForm({
      errors: form.errors,
      status: form.status,
      touched: form.touched,
      submitCount: form.submitCount,
      isSubmitting: form.isSubmitting,
      isValidating: form.isValidating,
      values: initialFormikValues ?? {},
    });
  }, [formShouldReset, JSON.stringify(caseForm)]);

  const { currentStep, maxOpenedStep } = useAppSelector(
    selectCaseFormStep(currentTaskId)
  );

  const { values, submitForm, setFieldValue } = form;

  const valuesRef = useRef(values);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  // Sync client_name from caseForm to formik form when client is updated
  useEffect(() => {
    if (formShouldReset || !caseForm?.client_name) {
      return;
    }

    // Only update if the formik value is different from caseForm value
    if (form.values.client_name !== caseForm.client_name) {
      setFieldValue('client_name', caseForm.client_name);
    }
  }, [
    caseForm?.client_name,
    formShouldReset,
    form.values.client_name,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!caseForm || formShouldReset) {
      return;
    }

    const aiFields = [
      'problem_summary',
      'chief_complaints',
      'internal_notes',
      'additional_notes',
      'client_name',
      'client_email',
      'pet_name',
      'species',
      'breed',
      'age',
      'sex',
      'weight',
      'medications',
      'allergies',
      'appetite',
      'thirst',
      'urination',
      'defecation',
      'vomiting',
      'diarrhea',
      'symptoms_description',
      'urgency_level',
    ];

    aiFields.forEach((field) => {
      const caseFormValue = caseForm[field];
      const currentFormValue = form.values[field];

      if (
        caseFormValue !== undefined &&
        caseFormValue !== null &&
        caseFormValue !== ''
      ) {
        if (
          field === 'problem_summary' &&
          (!currentFormValue || currentFormValue === '')
        ) {
          console.log(`[AI Sync] Updating ${field}:`, caseFormValue);
          form.setFieldValue(field, caseFormValue);
          form.setFieldValue('brief_description', caseFormValue);
        } else if (
          field === 'chief_complaints' &&
          (!currentFormValue || currentFormValue.length === 0)
        ) {
          console.log(`[AI Sync] Updating ${field}:`, caseFormValue);
          form.setFieldValue(field, caseFormValue);
        } else if (
          field !== 'problem_summary' &&
          field !== 'chief_complaints' &&
          !currentFormValue
        ) {
          console.log(`[AI Sync] Updating ${field}:`, caseFormValue);
          form.setFieldValue(field, caseFormValue);
        }
      }
    });

    // Match AI-extracted pet name to actual pet ID from client's pets
    if (caseForm.pet_name && client?.pets && client.pets.length > 0) {
      const aiPetName = String(caseForm.pet_name).toLowerCase().trim();
      const currentSelectedPets = form.values.pets || [];

      console.log(`[AI Pet Match] Starting match for "${caseForm.pet_name}"`, {
        aiPetName,
        currentSelectedPets,
        availablePets: client.pets.map((p) => ({
          id: p.id,
          name: p.name,
          species: p.species,
        })),
      });

      // Try exact match first
      let matchedPet = client.pets.find(
        (pet) => pet.name.toLowerCase().trim() === aiPetName
      );

      // If no exact match, try fuzzy match but be more careful
      if (!matchedPet) {
        // Look for pets where the AI name is contained in pet name or vice versa
        // But require at least 3 characters match to avoid false positives
        const fuzzyMatches = client.pets.filter((pet) => {
          const petNameLower = pet.name.toLowerCase();
          return (
            (petNameLower.includes(aiPetName) && aiPetName.length >= 3) ||
            (aiPetName.includes(petNameLower) && petNameLower.length >= 3)
          );
        });

        // If multiple fuzzy matches, log warning and pick first
        if (fuzzyMatches.length > 1) {
          console.warn(
            `[AI Pet Match] Multiple fuzzy matches found for "${caseForm.pet_name}":`,
            fuzzyMatches.map((p) => ({ id: p.id, name: p.name }))
          );
        }

        matchedPet = fuzzyMatches[0];
      }

      if (matchedPet) {
        // Check if we're changing the selection
        const isChangingSelection =
          currentSelectedPets.length > 0 &&
          !currentSelectedPets.includes(matchedPet.id);

        if (isChangingSelection) {
          console.warn(
            `[AI Pet Match] Changing pet selection from ${currentSelectedPets} to ${matchedPet.id}`
          );
        }

        console.log(`[AI Pet Match] Matched pet "${caseForm.pet_name}" to:`, {
          id: matchedPet.id,
          name: matchedPet.name,
          breed: matchedPet.breed,
          species: matchedPet.species,
        });

        form.setFieldValue('pets', [matchedPet.id]);
        form.setFieldValue('pet_id', matchedPet.id);
      } else {
        console.error(
          `[AI Pet Match] Could not find pet matching "${caseForm.pet_name}" in client's pets:`,
          client.pets.map((p) => ({
            id: p.id,
            name: p.name,
            species: p.species,
          }))
        );

        // Don't clear existing selection if we can't find a match
        if (currentSelectedPets.length === 0) {
          console.warn(
            `[AI Pet Match] No pet selected and couldn't match AI pet name`
          );
        }
      }
    }
  }, [caseForm, client?.pets]);

  useEffect(() => {
    if (task?.type === ActionQueueType.Voice && task?.willAutosubmitAt) {
      const now = Date.now();
      const timeRemaining = task?.willAutosubmitAt - now;
      const triggerIn = timeRemaining - 2000;

      const updateCaseAutoSubmitData = async () => {
        const formValues = valuesRef.current;
        const currentTask = taskRef.current;

        if (!currentTask || getQueueItemId(currentTask) !== taskId) {
          return;
        }

        const isDynamicFlowEnabled =
          currentTask?.h_is_dynamic_flow_enabled ?? false;

        let mergedFieldValues = passedStepsFieldValues ?? {};
        if (isDynamicFlowEnabled) {
          const flowId =
            passedStepsFieldValues?.own_fields?.$flowId ||
            caseForm?.own_fields?.$flowId ||
            undefined;
          const passedStepsArray =
            passedStepsFieldValues?.own_fields?.$passedSteps ||
            passedSteps?.map((step) => step.id) ||
            [];

          const preservedOwnFields: {
            $flowId?: number;
            $passedSteps?: string[];
          } = {};
          if (flowId) {
            preservedOwnFields.$flowId = flowId;
          }
          if (passedStepsArray && passedStepsArray.length > 0) {
            preservedOwnFields.$passedSteps = passedStepsArray;
          }
          const currentStepOwnFields = { ...(formValues?.own_fields || {}) };

          const { own_fields: _, ...formValuesWithoutOwnFields } =
            formValues || {};
          mergedFieldValues = {
            ...passedStepsFieldValues,
            ...formValuesWithoutOwnFields,
            own_fields: {
              ...(passedStepsFieldValues?.own_fields || {}),
              ...currentStepOwnFields,
              ...(preservedOwnFields.$flowId !== undefined && {
                $flowId: preservedOwnFields.$flowId,
              }),
              ...(preservedOwnFields.$passedSteps !== undefined && {
                $passedSteps: preservedOwnFields.$passedSteps,
              }),
            },
          };
        }

        const submissionValues = isDynamicFlowEnabled
          ? getDynamicCaseData({
              caseForm: caseForm ?? {},
              passedSteps: passedSteps ?? [],
              values: { ...formValues, client },
              passedStepsFieldValues: mergedFieldValues,
            })
          : {
              ...formValues,
              cvt_plan:
                formValues?.cvt_plan && formValues.cvt_plan.trim() !== ''
                  ? formValues.cvt_plan
                  : 'N/A',
            };

        if (isDynamicFlowEnabled && submissionValues.own_fields) {
          const flowId = mergedFieldValues?.own_fields?.$flowId;
          const passedStepsList = mergedFieldValues?.own_fields?.$passedSteps;

          if (flowId !== undefined) {
            submissionValues.own_fields.$flowId = flowId;
          }
          if (passedStepsList !== undefined) {
            submissionValues.own_fields.$passedSteps = passedStepsList;
          }
        }

        try {
          await dispatch(
            updateCase({
              silent: true,
              task: currentTask,
              isSubmitted: true,
              values: submissionValues,
              isDynamicFlow: isDynamicFlowEnabled,
            })
          ).unwrap();
        } catch (error) {
          console.error('[Auto-submit] Failed to auto-submit case:', error);
        }
      };

      if (triggerIn <= 0) {
        updateCaseAutoSubmitData();

        return;
      }

      const timeoutId = setTimeout(updateCaseAutoSubmitData, triggerIn);

      return () => clearTimeout(timeoutId);
    }
  }, [
    dispatch,
    task,
    taskId,
    values,
    caseForm,
    passedSteps,
    passedStepsFieldValues,
    client,
  ]);

  const allowToSaveForm = form.isValid && !form.isSubmitting;

  const updateCaseFormIfValid = useCallback(async () => {
    if (!allowToSaveForm) {
      return;
    }
    const formValues = valuesRef.current;
    const currentTask = taskRef.current;
    if (!currentTask || !formValues.id || !formValues.client_id) {
      return;
    }
    const prefix = `[case-auto-save](caseId:${formValues.id}`;
    try {
      await dispatch(
        updateCase({
          silent: true,
          hideLoading: true,
          task: currentTask,
          values: {
            ...formValues,
            client,
            brief_description: formValues.problem_summary,
          },
        })
      ).unwrap();
    } catch (error) {
      console.error(`${prefix} failed`, error);
    }
  }, [allowToSaveForm]);

  useEffect(() => {
    if (formShouldReset) {
      console.log(`form should reset, old ${formTaskId}, new ${taskId}`);
    }
  }, [formShouldReset]);

  useEffect(() => {
    resetSteps();
  }, [formShouldReset, resetSteps]);

  function getAllErrorMessagesWithPaths(obj: any): Record<string, string> {
    const result: Record<string, string> = {};

    function traverse(value: any, path: string = '') {
      if (typeof value === 'string') {
        result[path] = value;
      } else if (typeof value === 'object' && value !== null) {
        for (const key in value) {
          const newPath = path ? `${path}.${key}` : key;
          traverse(value[key], newPath);
        }
      }
    }
    traverse(obj);
    return result;
  }

  const submit = useCallback(async () => {
    const result = await submitForm();
    const { errors } = form;
    const errorStep = getCaseFormStepWithError(errors);
    console.log('[submitCase]: submit case form with values:', {
      form,
      errors,
      errorStep,
      isDynamicFlow,
    });

    if (form?.errors && Object.keys(form.errors).length > 0) {
      Sentry.captureMessage('Submission Error', {
        level: 'warning',
        extra: { form, errors: form.errors },
      });
      const newErrors = getAllErrorMessagesWithPaths(errors);

      // eslint-disable-next-line
      Object.entries(newErrors || {}).forEach(([key, value]) => {
        showErrorAlert(value);
      });
    }
    if (errorStep && currentTaskId && !isDynamicFlow) {
      console.log('[changeStep]: change step in submit', {
        step: errorStep,
        formId: currentTaskId,
      });
      dispatch(changeStep({ step: errorStep, formId: currentTaskId }));
    }

    return result;
  }, [submitForm, form.errors, isDynamicFlow, currentTaskId]);

  const valuesForUpdateForm =
    JSON.stringify(values) +
    taskId +
    authUser?.id +
    currentStep +
    maxOpenedStep;

  const [valuesRefToSaveLocally] = useDebounceValue(valuesForUpdateForm, 500);
  const [valuesRefToSaveOnServer] = useDebounceValue(valuesForUpdateForm, 3000);

  const formCaseId = values.id;
  const taskCaseId = task?.case_id;

  useEffect(() => {
    console.log(
      'task case form',
      JSON.stringify({ taskId, formCaseId, taskCaseId })
    );
  }, [taskId, formCaseId, taskCaseId]);

  const onSaveCaseForm = (updatedValues?: FormikValues) => {
    if (authUser?.id && taskId && task && !isTaskSwitching) {
      const initialCaseForm = getActionCenterCaseForm(task, authUser);
      const diff = Utils.Object.shallowDifference(
        initialCaseForm,
        updatedValues || valuesRef.current
      );

      if (!!updatedValues) {
        if (!updatedValues.problem_summary?.length) {
          diff.problem_summary = null;
        }
        if (
          !(
            updatedValues.chief_complaints &&
            updatedValues.chief_complaints.length
          ) &&
          !(
            initialCaseForm.chief_complaints &&
            initialCaseForm.chief_complaints.length
          )
        ) {
          diff.chief_complaints = initialCaseForm.chief_complaints;
        }
      }

      saveCaseForm({
        roomId: taskId,
        userId: authUser.id,
        data: {
          ...diff,
          current_step: currentStep,
          max_opened_step: maxOpenedStep,
        },
      });
    }
  };

  useEffect(() => {
    onSaveCaseForm();
  }, [valuesRefToSaveLocally]); // DON'T SET ANY OTHER DEPS - IT'S BREAK WHEN TASK SWITCHED, JUST ADD TO valuesJson

  useEffect(() => {
    updateCaseFormIfValid();
  }, [updateCaseFormIfValid, valuesRefToSaveOnServer]); // DON'T SET ANY OTHER DEPS - IT'S BREAK WHEN TASK SWITCHED, JUST ADD TO valuesJson

  const prevClientPetsRef = useRef<string>('');
  const prevClientIdRef = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    if (!isDynamicFlow || !client) {
      return;
    }

    // Reset the ref if client ID changed (e.g., switching tasks or coming back from pet edit)
    if (prevClientIdRef.current !== client.id) {
      prevClientIdRef.current = client.id;
      prevClientPetsRef.current = '';
    }

    const replaceData = getDynamicFormInitialValues(values, client);

    // Deduplicate clientPets by pet ID to prevent duplicates
    const newClientPets = (replaceData.clientPets || []).reduce(
      (acc: any[], pet: any) => {
        if (pet?.id && !acc.find((p) => p?.id === pet.id)) {
          acc.push(pet);
        }
        return acc;
      },
      []
    );

    // Create a string representation of pet IDs and their data to compare
    // Include pet data hash to detect when pet details change (not just IDs)
    const newClientPetsSignature = JSON.stringify(
      newClientPets
        .map((pet: any) => ({
          id: pet?.id,
          name: pet?.name,
          breed: pet?.breed,
          species: pet?.species,
        }))
        .sort((a: any, b: any) => (a.id || 0) - (b.id || 0))
    );

    // Only skip update if the signature is exactly the same (same pets with same data)
    // This ensures we update when:
    // 1. Pet count changes (incomplete data initially)
    // 2. Pet details change (after edit)
    // 3. New pets are added
    if (prevClientPetsRef.current === newClientPetsSignature) {
      return;
    }

    prevClientPetsRef.current = newClientPetsSignature;

    // Always update clientPets when client data changes to ensure we have the latest data
    // The deduplication above prevents actual duplicates
    setFieldValue('clientPets', newClientPets);

    // Always update client as it might have other changes
    setFieldValue('client', replaceData.client);
    if (newClientPets.length > 0) {
      setFieldValue('pet', replaceData.pet);
    }
  }, [isDynamicFlow, client, taskId, values?.clientPets, setFieldValue]);

  useEffect(() => {
    return () => {
      if (taskRef.current) {
        dispatch(
          updateForm({ task: taskRef.current, form: valuesRef.current })
        );

        updateCaseFormIfValid();
      }
    };
  }, [updateCaseFormIfValid]);

  return { ...form, onSaveCaseForm, dynamicFormProps, submitForm: submit };
};
